import {
  HttpTransportType,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import getHubAccessToken from "./hubAccessToken";

// En producción no volcamos el contenido de los mensajes (payloads con datos
// de mesas/pedidos) a la consola ni el log informativo de SignalR.
const IS_PRODUCTION = process.env.NODE_ENV === "production";

/**
 * Manager singleton de conexiones SignalR. Mantiene UNA conexión por URL compartida
 * entre todos los callers del front.
 *
 * Antes: cada llamada a CreateConnection (hooks useProductHub, useHubMesas, etc.)
 * construía un HubConnection nuevo. Con 2-3 componentes suscribiendo al mismo hub,
 * había 2-3 websockets abiertos al mismo endpoint del server, cada uno con su propio
 * listener. Cada evento del server → N callbacks → N×M fetches.
 *
 * Ahora: el manager guarda una entrada por URL con refcount. Los hooks piden la
 * conexión, añaden sus listeners, y al desmontar liberan el refcount. Cuando el
 * último caller se va, se cierra la conexión.
 *
 * ── Self-healing ──────────────────────────────────────────────────────────
 * `withAutomaticReconnect` de signalr SÓLO cubre drops desde el estado Connected.
 * No cubre:
 *   1. Fallo del start() inicial (server caído al boot).
 *   2. `onclose` definitivo tras agotar reintentos (con nuestra policy nunca
 *      pasa, pero los AbortError del navegador en background pueden colarse).
 *   3. La ventana de detección de zombies (30s de keep-alive timeout).
 *
 * Para garantizar que el sistema NO QUEDA NUNCA caído sin recuperación:
 *   - Watchdog global cada `WATCHDOG_INTERVAL_MS`: para cada entry con
 *     `wantsConnected === true` y `state === Disconnected`, fuerza un
 *     start() con backoff propio (1→2→4→…→60s). Limpia exactamente los 3
 *     casos del párrafo anterior.
 *   - `onclose` ya no es terminal: si seguimos queriendo la conexión, el
 *     watchdog la resucita.
 *   - Listener `visibilitychange → visible`: al volver a foreground, kick
 *     inmediato (no esperamos el siguiente tick del watchdog).
 *   - Listener `window.online`: idem al recuperar conectividad.
 *
 * `wantsConnected` se mantiene `true` mientras haya refCount > 0. Sólo
 * cuando el grace timer del release() vence sin que nadie haya re-adquirido
 * se pone a `false`, y a partir de ahí el watchdog ignora la entry.
 *
 * ── Logging ────────────────────────────────────────────────────────────────
 * Logging exhaustivo del ciclo de vida. Cada log lleva:
 *   - Prefijo `[Hub]`
 *   - Sufijo URL recortada (`hostname/path`).
 *   - Timestamp ISO + uptime de la conexión en segundos.
 *   - connectionId actual (si existe).
 *
 * Eventos: build, start ok/error, onreconnecting, onreconnected, onclose,
 * heartbeat (cada 30s), watchdog kick, visibility kick, online kick.
 */

// url -> {
//   connection, startPromise, refCount, closeTimer,
//   onReconnectListeners: Set<fn>,
//   createdAt, lastConnectedAt, reconnectingAt,
//   heartbeat: number | null,
//   wantsConnected: boolean,        // el watchdog sólo opera si true
//   starting: boolean,              // hay un start() en vuelo
//   startFailures: number,          // backoff del watchdog
//   lastStartAttemptAt: number | null,
// }
const connections = new Map();

const CLOSE_GRACE_MS = 250;
const HEARTBEAT_INTERVAL_MS = 30_000;
// Cada cuánto el watchdog escanea el mapa buscando entries `Disconnected`
// con `wantsConnected=true`. 5s es suficientemente reactivo para que el
// usuario apenas note el outage y suficientemente espaciado para no
// presionar al server cuando hay 30+ TPVs reconectando a la vez.
const WATCHDOG_INTERVAL_MS = 5_000;
/** Relanzar tras onclose fuera del callback (ver onclose). */
export const ONCLOSE_RESTART_DELAY_MS = 50;
/** «Reintentar» corta un intento en Connecting a partir de este tiempo. */
export const STUCK_CONNECTING_MS = 10_000;
/** Una conexión más joven que esto no se reinicia en una vuelta (no puede ser zombie). */
export const FRESH_CONNECTION_MS = 5_000;
/**
 * 2026-10-27 · Una conexión que recibió algo del servidor (evento o baliza de
 * 30 s) hace menos de esto está viva: una vuelta no la reinicia. En producción
 * los TPV con la pestaña en segundo plano se reiniciaban cada ~2 min (Chrome
 * espacia los temporizadores y la vuelta lo tomaba por congelación).
 */
export const ALIVE_RECENT_MS = 45_000;

// 2026-10-28 · Sin JWT el API solo deja entrar en el hub de inicio de sesión
// del comensal (y en los de los workers): cualquier otro responde 401. Las
// páginas públicas (carta, reservas) abrían `/hub/tpv` sin token y repetían el
// 401 sin fin (dispositivos `anon-*`). Ahora esos hubs esperan «aparcados» a
// que haya token (el watchdog los arranca en cuanto existe, sin red).
const ANONYMOUS_HUB_PATHS = [
  "/hub/startsessionhub",
  "/hub/printerworker",
  "/hub/moneybox",
  "/hub/soundhub",
  "/hub/cashdrawer",
];

export function hubAllowsAnonymous(url) {
  let path = "";
  try {
    path = new URL(url, "http://local").pathname.toLowerCase();
  } catch {
    path = String(url || "").toLowerCase();
  }
  return ANONYMOUS_HUB_PATHS.some((p) => path === p || path.startsWith(`${p}/`));
}

function canStartHub(url) {
  return hubAllowsAnonymous(url) || !!getHubAccessToken();
}

// Un start() fallido: con WebSockets el 401 no se distingue de un fallo de red;
// la sesión del restaurante decide (restaurantSessionRuntime) si preguntar al API.
let startFailureObserver = null;
export function setHubStartFailureObserver(fn) {
  startFailureObserver = typeof fn === "function" ? fn : null;
}

/** El hub `url` acaba de recibir un mensaje del servidor (prueba de vida). */
export function markConnectionAlive(url) {
  const entry = connections.get(url);
  if (entry) entry.lastAliveAt = performance.now();
}

if (typeof window !== "undefined") {
  window.__hubDebug = () => {
    const out = [];
    for (const [url, entry] of connections) {
      out.push({
        url: shortUrl(url),
        state: entry.connection.state,
        connectionId: entry.connection.connectionId || null,
        refCount: entry.refCount,
        wantsConnected: entry.wantsConnected,
        starting: entry.starting,
        startFailures: entry.startFailures,
        listeners: entry.onReconnectListeners.size,
        uptimeSec: entry.lastConnectedAt
          ? Math.round((performance.now() - entry.lastConnectedAt) / 1000)
          : null,
        ageSec: Math.round((performance.now() - entry.createdAt) / 1000),
      });
    }
    console.table(out);
    return out;
  };
  window.__hubDebugCfg = window.__hubDebugCfg || { heartbeat: true };
}

function shortUrl(url) {
  try {
    const u = new URL(url);
    return `${u.host}${u.pathname}`;
  } catch {
    return url;
  }
}

function ts() {
  return new Date().toISOString().substring(11, 23);
}

// Fase 5 · Observador de ciclo de vida (reconexiones, cierres, arranques)
// para el panel de depuración. Sin observador, coste cero.
let lifecycleObserver = null;
export function setHubLifecycleObserver(fn) {
  lifecycleObserver = typeof fn === "function" ? fn : null;
}
function emitLifecycle(url, event, extra = {}) {
  if (!lifecycleObserver) return;
  try { lifecycleObserver({ url, event, ...extra }); } catch { /* noop */ }
}

function logHub(url, level, msg, ...rest) {
  const entry = connections.get(url);
  const uptime = entry?.lastConnectedAt
    ? `${Math.round((performance.now() - entry.lastConnectedAt) / 1000)}s`
    : "—";
  const cid = entry?.connection?.connectionId
    ? entry.connection.connectionId.substring(0, 8)
    : "—";
  const tag = `[Hub ${shortUrl(url)} cid=${cid} up=${uptime} ${ts()}]`;
  // eslint-disable-next-line no-console
  (console[level] || console.log)(tag, msg, ...rest);
}

function buildConnection(url) {
  return new HubConnectionBuilder()
    .withUrl(url, {
      transport: HttpTransportType.WebSockets,
      logMessageContent: !IS_PRODUCTION,
      skipNegotiation: true,
      // El backend exige JWT en los hubs (salvo workers legacy y el inicio de
      // sesión del comensal) y decide los grupos por los claims del token.
      // Con WebSockets el cliente lo envía como ?access_token=; se evalúa en
      // cada (re)conexión, así que un token renovado se aplica solo.
      accessTokenFactory: () => getHubAccessToken(),
    })
    .withAutomaticReconnect({
      // Backoff exponencial: 1, 2, 4, 8, 16, 32, max 60s. Devuelve `number`
      // siempre — signalr nunca agota reintentos por sí mismo. Si pese a
      // todo onclose se dispara (AbortError raro en navegadores en
      // background), el watchdog del manager toma el relevo.
      nextRetryDelayInMilliseconds: (retryContext) => {
        const attempt = retryContext.previousRetryCount;
        const ms = Math.min(60000, 1000 * Math.pow(2, attempt));
        logHub(
          url,
          "warn",
          `nextRetryDelay attempt=${attempt + 1} delay=${ms}ms elapsedSinceFirstFail=${retryContext.elapsedMilliseconds}ms`,
          retryContext.retryReason,
        );
        // Reintentos automáticos fallidos (p. ej. token caducado tras un corte):
        // mismo aviso que un start() fallido.
        if (attempt > 0 && startFailureObserver) {
          try {
            startFailureObserver({ url, failures: attempt, error: retryContext.retryReason });
          } catch { /* nunca romper el reintento */ }
        }
        return ms;
      },
    })
    .configureLogging(IS_PRODUCTION ? LogLevel.Warning : LogLevel.Information)
    .build();
}

/**
 * Llama a `connection.start()` actualizando el estado del entry.
 * Centralizado para que tanto el arranque inicial como el watchdog (y
 * los kicks de visibility / online) usen la misma máquina de estado.
 *
 * Si ya hay un start en vuelo (`entry.starting === true`) reusa el
 * promise existente — evita `Failed to start the HttpConnection before
 * stop() was called` por starts concurrentes.
 */
function notifyReconnectListeners(url, connection) {
  const e = connections.get(url);
  e?.onReconnectListeners.forEach((cb) => {
    try {
      cb(connection);
    } catch (err) {
      logHub(url, "error", "onReconnect listener threw", err);
    }
  });
}

function startEntry(url, reason) {
  const entry = connections.get(url);
  if (!entry) return Promise.resolve();
  if (entry.starting && entry.startPromise) return entry.startPromise;
  if (entry.connection.state === HubConnectionState.Connected) {
    return Promise.resolve();
  }
  if (entry.connection.state === HubConnectionState.Connecting) {
    // Hay un start en vuelo iniciado fuera de este flujo; respetarlo.
    return entry.startPromise || Promise.resolve();
  }

  if (!canStartHub(url)) {
    // Sin token: no se abre el socket (sería un 401). Se reintenta al haberlo.
    if (!entry.parked) logHub(url, "info", `start aplazado: sin token (reason=${reason})`);
    entry.parked = true;
    entry.lastStartAttemptAt = performance.now();
    if (!entry.startPromise) entry.startPromise = Promise.resolve();
    return entry.startPromise;
  }
  const wasParked = !!entry.parked;
  entry.parked = false;

  entry.starting = true;
  entry.lastStartAttemptAt = performance.now();
  const startedAt = performance.now();
  logHub(
    url,
    "info",
    `start() invoked (reason=${reason} state=${entry.connection.state} failures=${entry.startFailures})`,
  );

  entry.startPromise = entry.connection
    .start()
    .then(() => {
      const e = connections.get(url);
      // Fase 3 · Un start() tras onclose/watchdog es una conexión NUEVA
      // con un hueco en el que se han podido perder eventos: se avisa a
      // los listeners de reconexión igual que en onreconnected (antes
      // solo onreconnected los disparaba y el estado se quedaba stale).
      const wasConnectedBefore = !!e?.everConnected || wasParked;
      if (e) {
        e.lastConnectedAt = performance.now();
        e.starting = false;
        e.startFailures = 0;
        e.everConnected = true;
      }
      if (wasConnectedBefore) notifyReconnectListeners(url, entry.connection);
      emitLifecycle(url, wasConnectedBefore ? "restarted" : "connected", {
        durationMs: Math.round(performance.now() - startedAt),
        reason,
      });
      logHub(
        url,
        "info",
        `start OK in ${Math.round(performance.now() - startedAt)}ms cid=${(entry.connection.connectionId || "").substring(0, 8)} state=${entry.connection.state}`,
      );
    })
    .catch((err) => {
      const e = connections.get(url);
      if (e) {
        e.starting = false;
        e.startFailures = (e.startFailures || 0) + 1;
      }
      logHub(
        url,
        "error",
        `start FAILED after ${Math.round(performance.now() - startedAt)}ms (failure #${e?.startFailures})`,
        err,
      );
      emitLifecycle(url, "start_failed", {
        failures: e?.startFailures,
        reason,
        error: err?.message || String(err || ""),
      });
      if (startFailureObserver) {
        try {
          startFailureObserver({ url, failures: e?.startFailures || 0, error: err });
        } catch { /* nunca romper el reintento */ }
      }
      // No re-lanzamos: el watchdog volverá a intentarlo.
    });

  return entry.startPromise;
}

/**
 * Watchdog global: una sola interval que escanea el mapa cada 5s y
 * resucita las conexiones que hayan quedado `Disconnected` mientras
 * `wantsConnected === true`. Backoff por entry usando `startFailures`.
 *
 * Dispara START sólo si:
 *   - state === Disconnected
 *   - wantsConnected === true
 *   - no hay start() en vuelo (starting === false)
 *   - ha pasado el delay de backoff desde el último intento fallido
 */
let watchdogInterval = null;

function ensureWatchdog() {
  if (watchdogInterval) return;
  watchdogInterval = setInterval(() => {
    if (connections.size === 0) return;
    for (const [url, entry] of connections) {
      if (!entry.wantsConnected) continue;
      if (entry.starting) continue;
      if (entry.connection.state !== HubConnectionState.Disconnected) continue;
      // Aparcado sin token: se arranca solo cuando lo haya (sin log ni red).
      if (entry.parked) {
        if (canStartHub(url)) startEntry(url, "token");
        continue;
      }

      const minDelay = Math.min(
        60000,
        1000 * Math.pow(2, entry.startFailures || 0),
      );
      const sinceLastAttempt = entry.lastStartAttemptAt
        ? performance.now() - entry.lastStartAttemptAt
        : Infinity;
      if (sinceLastAttempt < minDelay) continue;

      logHub(
        url,
        "warn",
        `WATCHDOG kick (state=Disconnected wants=true failures=${entry.startFailures || 0} sinceLastAttempt=${Math.round(sinceLastAttempt)}ms)`,
      );
      startEntry(url, "watchdog");
    }
  }, WATCHDOG_INTERVAL_MS);
}

// Listeners de visibilidad / red. Una sola subscripción global; cada
// vez que se dispara, kickeamos las entries Disconnected sin esperar al
// tick del watchdog (5s puede notarse al volver de la tab).
if (typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    if (connections.size === 0) return;
    logHub(
      "(global)",
      "info",
      "visibilitychange→visible — escaneando conexiones para kick inmediato",
    );
    kickAllDisconnected("visibility");
  });

  window.addEventListener("online", () => {
    if (connections.size === 0) return;
    logHub(
      "(global)",
      "info",
      "window.online — escaneando conexiones para kick inmediato",
    );
    kickAllDisconnected("online");
  });

  window.addEventListener("offline", () => {
    logHub("(global)", "warn", "window.offline — los hubs se desconectarán");
  });
}

/**
 * Un hub en `Reconnecting` espera su siguiente intento (backoff hasta 60 s):
 * se para y `onclose` lo arranca al momento con el backoff a cero. Así un
 * evento del usuario (volver a la pestaña, recuperar la WiFi, «Reintentar»)
 * no se queda hasta un minuto esperando al reintento automático.
 */
function retryReconnectingNow(url, entry, reason) {
  logHub(url, "info", `reconnect now (state=Reconnecting reason=${reason})`);
  entry.startFailures = 0;
  entry.lastStartAttemptAt = null;
  entry.connection.stop().catch((err) => logHub(url, "warn", "stop() error in reconnect now", err));
}

function kickAllDisconnected(reason) {
  for (const [url, entry] of connections) {
    if (!entry.wantsConnected) continue;
    if (entry.starting) continue;
    const state = entry.connection.state;
    if (state === HubConnectionState.Reconnecting) {
      retryReconnectingNow(url, entry, reason);
      continue;
    }
    if (state !== HubConnectionState.Disconnected) continue;
    // Reset del backoff: un evento del usuario (volver a la tab,
    // recuperar wifi) merece un intento inmediato sin penalización.
    entry.startFailures = 0;
    entry.lastStartAttemptAt = null;
    logHub(url, "info", `manual kick (reason=${reason})`);
    startEntry(url, reason);
  }
}

/**
 * «Reintentar» del aviso de tiempo real: intento inmediato de TODO hub caído
 * o reconectando (los conectados no se tocan). Devuelve cuántos se patearon.
 * Antes el botón llamaba a refreshStaleConnections, que se saltaba los
 * `Reconnecting` — justo el estado en que se ve el aviso — y no hacía nada.
 */
export function reconnectNow(reason = "manual") {
  let kicked = 0;
  const now = performance.now();
  for (const [url, entry] of connections) {
    if (!entry.wantsConnected) continue;
    const st = entry.connection.state;
    // Intento colgado (handshake sin respuesta): se corta y se relanza fuera
    // del callback. Antes «Reintentar» lo ignoraba y había que esperar a que
    // el servidor lo cancelara (30 s) y al siguiente backoff.
    if (
      st === HubConnectionState.Connecting &&
      entry.lastStartAttemptAt != null &&
      now - entry.lastStartAttemptAt >= STUCK_CONNECTING_MS
    ) {
      kicked += 1;
      logHub(url, "info", `reconnect now (state=Connecting stuck reason=${reason})`);
      entry.connection
        .stop()
        .catch(() => {})
        .finally(() => {
          setTimeout(() => {
            const cur = connections.get(url);
            if (!cur?.wantsConnected) return;
            cur.starting = false;
            cur.startPromise = null;
            cur.startFailures = 0;
            cur.lastStartAttemptAt = null;
            startEntry(url, reason);
          }, ONCLOSE_RESTART_DELAY_MS);
        });
      continue;
    }
    if (entry.starting) continue;
    if (st === HubConnectionState.Reconnecting || st === HubConnectionState.Disconnected) kicked += 1;
  }
  kickAllDisconnected(reason);
  return kicked;
}

/**
 * Vuelta tras congelación / segundo plano largo: un socket puede seguir
 * "Connected" en el cliente aunque el servidor ya lo haya dado por muerto
 * (zombie: no llegan eventos y nadie lo detecta hasta el serverTimeout).
 * Se reinician las conexiones Connected (stop → onclose → start inmediato,
 * que avisa a los listeners de reconexión y re-une a los grupos) y se
 * patean las Disconnected. Las que estaban reconectando reintentan ya
 * (sin esperar a su backoff).
 */
export function refreshStaleConnections(reason = "resume") {
  const now = performance.now();
  for (const [url, entry] of connections) {
    if (!entry.wantsConnected || entry.starting) continue;
    const state = entry.connection.state;
    if (
      state === HubConnectionState.Connected &&
      entry.lastConnectedAt != null &&
      now - entry.lastConnectedAt < FRESH_CONNECTION_MS
    ) {
      // 2026-10-26 · Recién conectada (p. ej. por la vuelta anterior, que
      // llegó junto a otro disparador: visible + resume/focus): no puede ser
      // zombie. Antes se cortaba al instante (realtime.disconnected con
      // connectedSeconds 0) y se repetía la vuelta entera.
      logHub(url, "info", `resume restart skipped (fresh connection, reason=${reason})`);
      continue;
    }
    if (
      state === HubConnectionState.Connected &&
      entry.lastAliveAt != null &&
      entry.lastConnectedAt != null &&
      entry.lastAliveAt >= entry.lastConnectedAt &&
      now - entry.lastAliveAt < ALIVE_RECENT_MS
    ) {
      // 2026-10-27 · Recibió algo hace nada: no es zombie.
      logHub(url, "info", `resume restart skipped (alive ${Math.round((now - entry.lastAliveAt) / 1000)}s ago, reason=${reason})`);
      continue;
    }
    if (state === HubConnectionState.Disconnected) {
      entry.startFailures = 0;
      entry.lastStartAttemptAt = null;
      logHub(url, "info", `resume kick (reason=${reason})`);
      startEntry(url, reason);
    } else if (state === HubConnectionState.Connected) {
      logHub(url, "info", `resume restart (reason=${reason})`);
      entry.connection.stop().catch((err) => logHub(url, "warn", "stop() error in resume restart", err));
    } else if (state === HubConnectionState.Reconnecting) {
      retryReconnectingNow(url, entry, reason);
    }
  }
}

/**
 * Reinicia UNA conexión (p. ej. `/hub/tpv` zombie: Connected pero sin
 * eventos ni beacon). Misma mecánica que refreshStaleConnections.
 */
export function restartConnection(url, reason = "zombie") {
  const entry = connections.get(url);
  if (!entry || !entry.wantsConnected || entry.starting) return false;
  const state = entry.connection.state;
  if (state === HubConnectionState.Disconnected) {
    entry.startFailures = 0;
    entry.lastStartAttemptAt = null;
    logHub(url, "warn", `restart kick (reason=${reason})`);
    startEntry(url, reason);
    return true;
  }
  if (state === HubConnectionState.Connected) {
    logHub(url, "warn", `restart (reason=${reason})`);
    emitLifecycle(url, "restart_requested", { reason });
    entry.connection.stop().catch((err) => logHub(url, "warn", "stop() error in restart", err));
    return true;
  }
  return false;
}

/** Estado actual de cada hub (para el aviso de tiempo real). */
export function getConnectionStates() {
  const out = [];
  for (const [url, entry] of connections) {
    if (!entry.wantsConnected) continue;
    out.push({ url, state: entry.connection.state });
  }
  return out;
}

/**
 * Adquiere (o crea si no existe) la conexión compartida para la URL.
 */
export function acquireConnection(url) {
  let entry = connections.get(url);

  if (!entry) {
    const connection = buildConnection(url);
    // Timeouts más generosos para reducir reconexiones espurias:
    // - Azure App Service / Front Door / proxies suelen tener idle-timeout
    //   de ~60-120 s. Con keepAlive 15 s enviábamos ping suficiente, pero
    //   una pausa del navegador (background tab, GC) podía saltar la
    //   ventana y el proxy cortaba.
    // - server 90 s = tolerancia de 3 keep-alives sin respuesta antes de
    //   considerar la conexión perdida.
    // - keepAlive 10 s = pings más frecuentes → más difícil que un idle
    //   timeout de 60 s cierre la conexión.
    // Con el HAR anterior veíamos 4 aperturas/hub en 4 min (~1/min);
    // esto debería reducirse a 1 apertura + 0 reconexiones en escenarios
    // normales.
    connection.serverTimeoutInMilliseconds = 90000;
    connection.keepAliveIntervalInMilliseconds = 10000;

    entry = {
      connection,
      startPromise: null,
      refCount: 0,
      closeTimer: null,
      onReconnectListeners: new Set(),
      everConnected: false,
      createdAt: performance.now(),
      lastConnectedAt: null,
      reconnectingAt: null,
      heartbeat: null,
      wantsConnected: true,
      starting: false,
      startFailures: 0,
      lastStartAttemptAt: null,
    };

    logHub(
      url,
      "info",
      `build serverTimeout=${connection.serverTimeoutInMilliseconds}ms keepAlive=${connection.keepAliveIntervalInMilliseconds}ms`,
    );

    connection.onreconnecting((error) => {
      const e = connections.get(url);
      if (e) e.reconnectingAt = performance.now();
      emitLifecycle(url, "reconnecting", { error: error?.message || null });
      logHub(
        url,
        "warn",
        `onreconnecting (state=${connection.state}) error=`,
        error,
      );
    });

    connection.onreconnected((connectionId) => {
      const e = connections.get(url);
      const id = connectionId || connection.connectionId;
      const downtimeMs = e?.reconnectingAt
        ? Math.round(performance.now() - e.reconnectingAt)
        : null;
      if (e) {
        e.lastConnectedAt = performance.now();
        e.reconnectingAt = null;
        e.startFailures = 0;
      }
      logHub(
        url,
        "info",
        `onreconnected newCid=${(id || "").substring(0, 8)} downtime=${downtimeMs}ms listeners=${e?.onReconnectListeners.size ?? 0}`,
      );
      notifyReconnectListeners(url, connection);
      emitLifecycle(url, "reconnected", { durationMs: downtimeMs });
    });

    // onclose ya no es terminal: el watchdog detectará state=Disconnected
    // mientras wantsConnected=true y volverá a llamar start(). Aquí sólo
    // resetamos el state interno y dejamos rastro en el log.
    connection.onclose((error) => {
      const e = connections.get(url);
      const wasConnected = e?.lastConnectedAt != null;
      const lifeSec = wasConnected
        ? Math.round((performance.now() - e.lastConnectedAt) / 1000)
        : null;
      logHub(
        url,
        "warn",
        `onclose wasConnected=${wasConnected} lifeSec=${lifeSec} wants=${e?.wantsConnected} → watchdog tomará el relevo si wants=true`,
        error,
      );
      emitLifecycle(url, "closed", { lifeSec, error: error?.message || null });
      if (e) {
        e.lastConnectedAt = null;
        e.reconnectingAt = null;
        e.starting = false;
      }
      // Kick inmediato si seguimos queriendo la conexión — no esperamos
      // al tick del watchdog (5s) para volver. Si falla, el watchdog ya
      // se encargará con backoff.
      if (e?.wantsConnected) {
        // Reset failures: este onclose puede ser un caso fortuito
        // (AbortError, transporte nuevo). Si vuelve a fallar, el
        // .catch incrementa failures de nuevo.
        e.startFailures = 0;
        // 2026-10-08 · NUNCA start() dentro del propio onclose: tras un
        // stop() (vuelta de segundo plano, «Reintentar», zombie) el cliente
        // de SignalR aún no ha soltado el transporte viejo y el handshake
        // se enviaba por un transporte `undefined` → el servidor lo cancelaba
        // a los 30 s («Handshake was canceled» + `TypeError ... 'send'` en
        // _sendLoop) y el hub se quedaba sin conectar. Reproducido contra el
        // API; con un tick de diferencia reconecta en ~250 ms.
        setTimeout(() => {
          const cur = connections.get(url);
          if (cur?.wantsConnected) startEntry(url, "onclose");
        }, ONCLOSE_RESTART_DELAY_MS);
      }
    });

    // Heartbeat — mismo que antes.
    entry.heartbeat = setInterval(() => {
      if (
        typeof window !== "undefined" &&
        window.__hubDebugCfg?.heartbeat === false
      )
        return;
      const e = connections.get(url);
      if (!e) return;
      const stateLabel =
        e.connection.state === HubConnectionState.Connected
          ? "✓ Connected"
          : e.connection.state === HubConnectionState.Reconnecting
            ? "↻ Reconnecting"
            : e.connection.state === HubConnectionState.Disconnected
              ? "✗ Disconnected"
              : `? ${e.connection.state}`;
      logHub(
        url,
        e.connection.state === HubConnectionState.Connected ? "log" : "warn",
        `heartbeat ${stateLabel} refCount=${e.refCount} wants=${e.wantsConnected} failures=${e.startFailures}`,
      );
    }, HEARTBEAT_INTERVAL_MS);

    connections.set(url, entry);

    // Arranca la conexión por primera vez. Si falla, el watchdog
    // reintentará con backoff — antes el sistema se quedaba muerto si
    // el primer start() fallaba (server arrancando, etc.).
    startEntry(url, "initial");
    ensureWatchdog();
  }

  if (entry.closeTimer) {
    clearTimeout(entry.closeTimer);
    entry.closeTimer = null;
    logHub(url, "info", "closeTimer cancelled (re-acquire within grace)");
  }

  // Si la entry había sido marcada como "no la queremos" (caso muy
  // raro: closeAllConnections + acquire concurrente) la rescatamos.
  entry.wantsConnected = true;

  // Si el WS ya está caído (puede haber ocurrido durante el grace), kick
  // inmediato para que el caller no espere al watchdog.
  if (
    entry.connection.state === HubConnectionState.Disconnected &&
    !entry.starting
  ) {
    entry.startFailures = 0;
    startEntry(url, "acquire-while-disconnected");
  }

  entry.refCount++;
  logHub(url, "log", `acquire refCount=${entry.refCount}`);
  return entry;
}

/**
 * Registra un callback que se ejecutará cada vez que la conexión se
 * recupere desde un reconnect. Devuelve un función `unsubscribe`.
 */
export function addReconnectListener(url, fn) {
  const entry = connections.get(url);
  if (!entry) {
    logHub(url, "warn", "addReconnectListener: no hay entry para esta url");
    return () => {};
  }
  entry.onReconnectListeners.add(fn);
  logHub(
    url,
    "log",
    `addReconnectListener total=${entry.onReconnectListeners.size}`,
  );
  return () => {
    const e = connections.get(url);
    e?.onReconnectListeners.delete(fn);
  };
}

/**
 * Libera una referencia a la conexión. Si llega a 0, programa el cierre
 * tras `CLOSE_GRACE_MS`. Si nadie re-adquiere antes, marca
 * `wantsConnected = false` (el watchdog deja de operar sobre ella) y
 * llama stop().
 */
export function releaseConnection(url) {
  const entry = connections.get(url);
  if (!entry) return;

  entry.refCount--;
  logHub(url, "log", `release refCount=${entry.refCount}`);
  if (entry.refCount > 0) return;

  entry.closeTimer = setTimeout(async () => {
    if (entry.refCount > 0) {
      entry.closeTimer = null;
      logHub(url, "log", "closeTimer fired but refCount>0, skip");
      return;
    }

    // A partir de aquí: nadie quiere esta conexión. El watchdog ya no
    // intentará resucitarla aunque caiga.
    entry.wantsConnected = false;
    connections.delete(url);
    if (entry.heartbeat) {
      clearInterval(entry.heartbeat);
      entry.heartbeat = null;
    }
    logHub(url, "info", "stopping connection (refCount=0, wants=false)");
    try {
      await entry.startPromise;
    } catch {
      /* swallow */
    }
    try {
      await entry.connection.stop();
      logHub(url, "info", "stopped cleanly");
    } catch (err) {
      logHub(url, "warn", "stop() error", err);
    }
  }, CLOSE_GRACE_MS);
}

/**
 * Fuerza el cierre de todas las conexiones — úsese al logout / cambio de usuario.
 */
export function closeAllConnections() {
  for (const [url, entry] of connections) {
    entry.wantsConnected = false; // bloquea al watchdog
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
    if (entry.heartbeat) {
      clearInterval(entry.heartbeat);
      entry.heartbeat = null;
    }
    logHub(url, "info", "closeAllConnections — forcing stop");
    (async () => {
      try {
        await entry.startPromise;
      } catch {
        /* ignored */
      }
      try {
        await entry.connection.stop();
      } catch (err) {
        logHub(url, "warn", "stop() error in closeAll", err);
      }
    })();
  }
  connections.clear();
}
